import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Button,
  FormHelperText,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { StockStatusResponse } from "../types/api.types";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

export interface FilterStocksFormData {
  code: string;
  statusId: string;
  dateFrom: string;
  dateTo: string;
}

interface FilterStocksProps {
  searchFilters?: FilterStocksFormData | null;
  onCancel?: () => void;
  onFilter?: (data: FilterStocksFormData) => void;
  statuses: StockStatusResponse[];
}

const FilterStocks = ({
  searchFilters,
  onCancel,
  onFilter,
  statuses,
}: FilterStocksProps) => {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FilterStocksFormData>({
    defaultValues: {
      code: "",
      statusId: "",
      dateFrom: "",
      dateTo: "",
    },
  });

  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");

  useEffect(() => {
    if (searchFilters) {
      reset({
        code: searchFilters.code || "",
        statusId: searchFilters.statusId || "",
        dateFrom: searchFilters.dateFrom || "",
        dateTo: searchFilters.dateTo || "",
      });
    }
  }, [searchFilters, reset]);

  const onSubmitHandler = (data: FilterStocksFormData) => {
    if (onFilter) {
      onFilter(data);
    }
  };

  const handleCancel = () => {
    reset();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        {/* Código Interno */}
        <Box sx={{ flex: 1, minWidth: "200px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Código Interno
          </Typography>
          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Buscar código..."
                size="small"
                fullWidth
              />
            )}
          />
        </Box>

        {/* Estado Dropdown */}
        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Estado
          </Typography>
          <Controller
            control={control}
            name="statusId"
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <Select {...field} displayEmpty>
                  <MenuItem value="">Todos</MenuItem>
                  {statuses.map((status) => (
                    <MenuItem
                      key={status.stockStatusId}
                      value={status.stockStatusId}
                    >
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>

        {/* Date Picker - Fecha Desde */}
        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Fecha Estimada Desde
          </Typography>
          <Controller
            control={control}
            name="dateFrom"
            rules={{
              validate: {
                required: (value) => {
                  if (dateTo && !value) {
                    return "La Fecha Estimada desde es requerida si Fecha Hasta está llena";
                  }
                  return true;
                },
                lessThanDateTo: (value) => {
                  if (value && dateTo && value > dateTo) {
                    return "La Fecha Estimada Desde debe ser menor que Fecha Estimada Hasta";
                  }
                  return true;
                },
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!errors.dateFrom}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue: Dayjs | null) => {
                      field.onChange(
                        newValue ? newValue.format("YYYY-MM-DD") : "",
                      );
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                  />
                </LocalizationProvider>
                {errors.dateFrom && (
                  <FormHelperText>{errors.dateFrom.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        {/* Date Picker - Fecha Hasta */}
        <Box sx={{ flex: 0.8, minWidth: "180px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Fecha Estimada Hasta
          </Typography>
          <Controller
            control={control}
            name="dateTo"
            rules={{
              validate: {
                required: (value) => {
                  if (dateFrom && !value) {
                    return "La Fecha Estimada Hasta es requerida si Fecha Estimada Desde está completa";
                  }
                  return true;
                },
                greaterThanDateFrom: (value) => {
                  if (value && dateFrom && value < dateFrom) {
                    return "La Fecha Estimada Hasta debe ser mayor que Fecha Estimada Desde";
                  }
                  return true;
                },
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth error={!!errors.dateTo}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue: Dayjs | null) => {
                      field.onChange(
                        newValue ? newValue.format("YYYY-MM-DD") : "",
                      );
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                  />
                </LocalizationProvider>
                {errors.dateTo && (
                  <FormHelperText>{errors.dateTo.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button variant="outlined" color="primary" onClick={handleCancel}>
          Limpiar
        </Button>
        <Button type="submit" variant="contained" color="primary">
          Filtrar
        </Button>
      </Box>
    </form>
  );
};

export default FilterStocks;
